/**
 * Portal Edge font slot: supported web font family select, optional zip or single-font upload on the same row, and
 * custom-package display with delete (see {@link PortalEdgeFontPackageDisplay}). When the API provides `custom_font_upload_original_name`, that label is shown;
 * otherwise multiple inner files show a summary line with a tooltip. Custom mode is implied when a package is uploaded or saved (no toggle).
 */

import { FC, useState } from 'react';
import { observer } from 'mobx-react';
import FontBody2 from '../../../../../components/fonts/font-body2/font-body2';
import { FONT_COLOR } from '../../../../../components/fonts/font-props';
import { FormFieldLine } from '../../../../../components/form-field-line/form-field-line';
import FileUpload, { FILE_UPLOAD_VARIANTS } from '../../../../../components/file-upload/file-upload';
import { EdgeFontSlot } from '../../../../../models/User';
import { IOption, SelectDropdown } from '../../../../../components/inputs/select-dropdown/select-dropdown';
import CustomizationEdgeFontsStore from '../../../../../stores/customizations/customization-edge-fonts-store';
import PortalEdgeFontPackageDisplay from './portal-edge-font-package-display';
import FontPackageFileActionsList from './font-package-file-actions-list';
import customizationStore from '../../../../../stores/customizations/customization-store';

const MAX_FONT_ZIP_BYTES = 5_000_000;
const MAX_SINGLE_FONT_BYTES = 2_000_000;

/** MIME types browsers may set for zip or individual font uploads (must stay aligned with FontPackageService::ALLOWED_EXTENSIONS on the API). */
const EDGE_FONT_UPLOAD_MIME_TYPES = [
    'application/zip',
    'application/x-zip-compressed',
    'font/woff2',
    'font/woff',
    'font/ttf',
    'font/otf',
    'application/font-woff',
    'application/x-font-woff',
    'application/x-font-ttf',
    'application/x-font-truetype',
    'application/x-font-opentype',
    'application/x-font-otf',
    'application/font-sfnt',
    'application/octet-stream',
    '',
];

interface IPortalEdgeFontSectionProps {
    label: string;
    slot: EdgeFontSlot;
    supportedFontFamilyOptions: IOption[];
    supportedFontFamiliesLoading: boolean;
    showCustomZipUpload: boolean;
    isSaving: boolean;
}

const PortalEdgeFontSection: FC<IPortalEdgeFontSectionProps> = observer(
    ({
        label,
        slot,
        supportedFontFamilyOptions,
        supportedFontFamiliesLoading,
        showCustomZipUpload,
        isSaving,
    }) => {
        const fontsStore = customizationStore.getEdgeFontsStore();
        const selectedFontFamily = fontsStore.getFamilyForSlot(slot);
        const useCustomFont = fontsStore.useCustomFontForSlot(slot);
        const customFontFiles = fontsStore.customFontFilesForSlot(slot);
        const customFontUploadOriginalName = fontsStore.customFontUploadOriginalNameForSlot(slot);
        const hasPendingPackage = fontsStore.hasPendingForSlot(slot);
        const pendingPackageFileName = fontsStore.pendingDisplayNameForSlot(slot);
        const [uploadError, setUploadError] = useState<string | undefined>();

        const uploadFontPackage = (
            fileData: File,
        ) => {
            if (!fileData) {
                return;
            }

            setUploadError(undefined);

            const nameLower = fileData.name.toLowerCase();
            const isZip = nameLower.endsWith('.zip');
            if (isZip && fileData.size > MAX_FONT_ZIP_BYTES) {
                setUploadError(
                    `Zip font package exceeds maximum size (${MAX_FONT_ZIP_BYTES / 1_000_000}MB).`,
                );
                return;
            }
            if (!isZip && fileData.size > MAX_SINGLE_FONT_BYTES) {
                setUploadError(
                    `Font file exceeds maximum size (${MAX_SINGLE_FONT_BYTES / 1_000_000}MB).`,
                );
                return;
            }

            fontsStore.uploadFontPackageForSlot(slot, fileData);
        };

        const wrapperClassName = (customFontAvailable: boolean, hasUploadedFiles: boolean) => {
            let className = 'portal-edge-font-slot-row';

            if (customFontAvailable) {
                className += ' rich-actions';
            }

            if (hasUploadedFiles) {
                className += ' rich-actions--deletable';
            }

            return className;
        }

        const hasSavedFiles = customFontFiles.length > 0;
        const selectedFontFamilyOption = supportedFontFamilyOptions.find(
            (o) => o.value === selectedFontFamily,
        );

        const hasRightColumn =
            showCustomZipUpload &&
            (!useCustomFont || hasSavedFiles || hasPendingPackage);

        const rowClassName = [
            wrapperClassName(useCustomFont, hasSavedFiles || hasPendingPackage),
            !hasRightColumn ? 'portal-edge-font-slot-row--select-only' : '',
        ]
            .filter(Boolean)
            .join(' ');

        return (
            <FormFieldLine label={label}>
                <div className={rowClassName}>
                    <div className="portal-edge-font-slot-row__select">
                        <SelectDropdown
                            options={supportedFontFamilyOptions}
                            value={selectedFontFamilyOption}
                            onChange={(opt) => opt && fontsStore.setFontForSlot(slot, String(opt.value))}
                            isLoading={supportedFontFamiliesLoading}
                            isSearchable
                            placeholder="Select a font"
                            disabled={useCustomFont || isSaving}
                        />
                    </div>
                    {hasRightColumn && (
                        <div className="portal-edge-font-slot-row__actions">
                            {showCustomZipUpload && !useCustomFont && (
                                <>
                                    <FontPackageFileActionsList>
                                        <FileUpload
                                            fileTypes={EDGE_FONT_UPLOAD_MIME_TYPES}
                                            acceptedFileFormats=".zip,.woff2,.woff,.ttf,.otf"
                                            maxFileSize={MAX_FONT_ZIP_BYTES}
                                            readType="dataUrl"
                                            uploadFile={uploadFontPackage}
                                            onError={(error) => setUploadError(error.message)}
                                            buttonText="Upload"
                                            variant={FILE_UPLOAD_VARIANTS.Secondary}
                                            disabled={isSaving}
                                        />
                                    </FontPackageFileActionsList>
                                    {uploadError && (
                                        <FontBody2 color={FONT_COLOR.SEMANTIC_DANGER}>{uploadError}</FontBody2>
                                    )}
                                </>
                            )}

                            {showCustomZipUpload && (hasSavedFiles || hasPendingPackage) && (
                                <PortalEdgeFontPackageDisplay
                                    hasPendingPackage={hasPendingPackage}
                                    pendingPackageFileName={pendingPackageFileName}
                                    customFontUploadOriginalName={customFontUploadOriginalName}
                                    customFontFiles={customFontFiles}
                                    onDeleteCustomFont={() => fontsStore.deleteCustomFontForSlot(slot)}
                                    isLoading={isSaving}
                                />
                            )}
                        </div>
                    )}
                </div>
            </FormFieldLine>
        );
    },
);

export default PortalEdgeFontSection;
